import {
  useMemo,
  CSSProperties,
  ReactElement,
  Fragment,
  ReactNode
} from 'react';
import cn from 'clsx';
import { constant, get } from 'lodash-es';

import Spinner from 'components/ui/Spinner';
import Pagination from 'components/ui/Pagination';
import { useIsMobile } from 'hooks/useIsMobile';
import { useExpanded, ExpandContext } from 'hooks/useExpanded';
import { Trans } from '@lingui/react/macro';
import { Column } from './types';
import { TableHead } from './TableHead';
import { TableRow } from './TableRow';
import { TableCell, TableCellProps } from './TableCell';

import styles from './Table.module.scss';
import { TableBase } from './TableBase';

export interface TableProps<T> {
  /** Список колонок */
  columns: Array<Column<T>>;
  className?: string;
  /** Свойство из объекта которое будет использоваться в качестве ключа для строки */
  dataKey?: string;
  /** Функция формирования ссылки для строки */
  rowLink?: TableCellProps<T>['rowLink'];
  /** Стили для строки исходя из данных записи */
  rowStyle?: (d: T) => CSSProperties | undefined;
  /** Данные */
  data: T[] | undefined;
  /** Использовать пагинацию или нет */
  pagination?: boolean;
  /** Переопределние лимитов для пагинации */
  limits?: number[];
  /** Состояние загрузки данных */
  loading?: boolean;
  /** Текущий лимит записей на страницу */
  limit?: number;
  /** Обработчик изменения лимита */
  setLimit?: (limit: number) => void;
  /** Переход на предыдущую страницу */
  goPrevious?: () => void;
  /** Переход на следующую страницу */
  goNext?: () => void;
  /** Класс для строки исходя из данных */
  getRowClassName?: (data: T) => string | undefined;
  /** Раскрашивать ли строки в разные цвета. Если да - то чередуются прозрачный и синий цвет темы */
  striped?: boolean;
  /** Раскрывающийся список */
  expand?: (props: { data: T }) => ReactElement;
  /** Заголовок таблицы */
  tableHead?: ReactNode;
  /** Перестраивать колонки в мобильную раскладку */
  responsive?: boolean;
}

/**
 * Тип T выводится из других типов, которые используются внутри Table
 * Скорее всего возьмется из типа колонок columns (так он выводится во многих местах в проекте)
 * @returns
 */
export function Table<T>({
  columns: rawColumns,
  data = [],
  loading,
  className,
  dataKey = 'Id',
  rowLink,
  rowStyle = () => undefined,
  setLimit,
  pagination = true,
  goNext,
  goPrevious,
  limits,
  limit,
  getRowClassName = constant(''),
  striped = true,
  expand: Expand,
  tableHead,
  responsive = true
}: TableProps<T>) {
  const isMobile = useIsMobile() && responsive;

  const columns: Column<T>[] = useMemo(
    () =>
      rawColumns.filter((c: Column<T>) =>
        c.condition ? c.condition(data || []) : true
      ),
    [data, rawColumns]
  );

  const layout = useMemo(() => {
    if (!isMobile) {
      return [columns.filter((c) => !c.skipDesktop)];
    }
    const [firstLine, ...secondsLines] = columns.reduce((acc, c) => {
      if (c.skipMobile) return acc;
      const [first = [], ...otherLines] = acc;
      if (!c.under) {
        return [[...first, c], ...otherLines];
      }
      let slotSet = false;
      const newLines = otherLines.map((line) => {
        const underSlotIsExists = line.some((col) => col.under === c.under);
        if (underSlotIsExists || slotSet) {
          return line;
        }
        slotSet = true;
        return [...line, c];
      });
      const resultLines = slotSet ? newLines : [...newLines, [c]];
      return [first, ...resultLines];
    }, [] as Column<T>[][]);

    const secondSorted = firstLine.reduce(
      (acc, col) => {
        let skipLines = col.lines || 0;
        const blankLines: Array<Column<T> | undefined | null> = [];
        while (skipLines > 1) {
          skipLines -= 1;
          blankLines.push(null);
        }
        const lines = secondsLines.map((l) => {
          const lineColumn = l.find((c) => c.under === col.name);
          return lineColumn || undefined;
        });
        return blankLines
          .concat(lines)
          .map((l, idx) => [...(acc[idx] || []), l]);
      },
      [] as Array<Array<Column<T> | undefined | null>>
    );

    return [firstLine, ...secondSorted];
  }, [columns, isMobile]);

  const [first, ...next] = layout as [
    Column<T>[],
    Array<Column<T> | undefined>
  ];

  const fixed = first.some((c) => c.size);
  const noData = useMemo(
    () => !loading && data && data.length === 0,
    [data, loading]
  );
  const [isExpanded, toggleExpand] = useExpanded();

  const expandValue = useMemo(
    () => ({ isExpanded, toggleExpand }),
    [isExpanded, toggleExpand]
  );

  if (!data) {
    return <Spinner />;
  }

  return (
    <ExpandContext.Provider value={expandValue}>
      <div className={cn(className)}>
        <TableBase fixed={fixed}>
          {tableHead || <TableHead columns={first} isMobile={isMobile} />}
          <tbody>
            {data.map((d, idx) => {
              const key = get(d, dataKey) as string;
              const expandRow = () => {
                toggleExpand(key);
              };
              return (
                <Fragment key={key}>
                  <TableRow
                    isEven={!((idx + 1) % 2)}
                    style={rowStyle(d)}
                    className={getRowClassName(d)}
                    striped={striped}
                    onClick={rowLink ? undefined : expandRow}
                  >
                    <TableRow.Line>
                      {first.map((c) => (
                        <TableCell
                          key={c.name as string}
                          column={c}
                          data={d}
                          rowLink={rowLink}
                          rows={layout.length}
                          isMobile={isMobile}
                        />
                      ))}
                    </TableRow.Line>
                    {next.map((line, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <TableRow.Line key={index}>
                        {line.map((c, i) => (
                          <TableCell
                            data-cell={c?.name as string}
                            key={(c?.name as string) ?? i}
                            column={c}
                            data={d}
                            rowLink={rowLink}
                            rows={layout.length}
                            isMobile={isMobile}
                          />
                        ))}
                      </TableRow.Line>
                    ))}
                  </TableRow>
                  {Expand && isExpanded(key) && (
                    <TableRow
                      isEven={!((idx + 1) % 2)}
                      style={rowStyle(d)}
                      className={cn(getRowClassName(d), 'no-border')}
                      striped={striped}
                    >
                      <TableRow.Line>
                        <td />
                        <td colSpan={first.length - 1}>
                          <Expand data={d} />
                        </td>
                      </TableRow.Line>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className={styles.empty} colSpan={99}>
                {noData && <Trans>Нет данных</Trans>}
                {loading ? (
                  <div className={styles.loading}>
                    <Spinner />
                  </div>
                ) : null}
              </td>
            </tr>
          </tfoot>
        </TableBase>
        {pagination && (goNext || goPrevious) && (
          <Pagination
            className={styles.paging}
            limits={limits}
            onNext={goNext}
            onPrevious={goPrevious}
            value={limit}
            onLimitChange={setLimit}
          />
        )}
      </div>
    </ExpandContext.Provider>
  );
}

Table.TableRow = TableRow;
Table.TableBase = TableBase;
